import React, { useState } from "react"
import { InputCheckbox } from "../atoms/InputCheckbox.atom"
import { clsx } from "../../utils/strings.utils"
import InputText from "../atoms/InputText.atom"
import Button from "../atoms/Button.atom"
import ProgressSpinner from "../atoms/misc/ProgressSpinner.atom"
import PropTypes from "prop-types"
import { dbg } from "../../utils/general.utils"
import _ from "lodash"

const buttonDropdownHeight = 63

/**
 * @template T
 * @param {import('./InlineMultiSelectDropdown.molecule').InlineMultiSelectDropdownProps<T>} props
 */
const InlineMultiSelectDropdown = ({
  value,
  itemTemplate,
  optionLabel,
  options,
  onChange,
  optionValue,
  placeholder,
  selectedItemsLabel,
  maxSelectedLabels = Infinity,
  maxHeight = 480,
  optionGroupLabel,
  optionGroupChildren,
  optionGroupTemplate,
  selectedItemTemplate,
  className,
  emptyFilterMessage = "Sin resultados.",
  hasSearchBar = false,
  loading = false,
  disabled = false,
  hideToggleButton = false,
  initiallyExpanded = false,
  customSuffixHeaderIcon,
  autoFocusSearchBar = true,
}) => {
  dbg(() => {
    if (optionGroupLabel || optionGroupChildren || optionGroupTemplate) {
      console.assert(!!optionGroupLabel, "missing optionGroupLabel")
      console.assert(!!optionGroupChildren, "missing optionGroupChildren")
      console.assert(!!optionGroupTemplate, "missing optionGroupTemplate")
    }

    if (maxSelectedLabels !== undefined && !hideToggleButton) {
      console.assert(
        !!selectedItemTemplate,
        "selectedItemTemplate is required when maxSelectedLabels is provided"
      )
    }

    if (hideToggleButton && selectedItemTemplate !== undefined) {
      console.error(
        "<InlineMultiSelectDropdown> does not need a 'selectedItemTemplate' prop when hiding the toggle button!"
      )
    }
  })
  const isOptionGroup = !!optionGroupLabel
  const [isFocused, setIsFocused] = useState(false)
  const [filter, setFilter] = useState("")
  const [isContentVisible, setIsContentVisible] = useState(initiallyExpanded)

  const renderLabels = () => {
    if (value.length === 0) return placeholder

    if (value.length <= maxSelectedLabels && !!selectedItemTemplate) {
      if (isOptionGroup) {
        const items = []
        for (let i = 0; i < value.length; i++) {
          const v = value[i]
          let item
          options.forEach((option) => {
            option[optionGroupChildren].forEach((el) => {
              if (el[optionValue] === v) {
                item = el
              }
            })
          })

          if (!item) {
            continue
          }
          items.push(
            <React.Fragment key={i}>
              {selectedItemTemplate(item, i)}
            </React.Fragment>
          )
        }

        return <div className="flex gap-1">{items}</div>
      } else {
        const items = []
        for (let i = 0; i < value.length; i++) {
          const v = value[i]
          items.push(
            <React.Fragment key={i}>
              {selectedItemTemplate(v, i)}
            </React.Fragment>
          )
        }
        return <div className="flex gap-1">{items}</div>
      }
    } else {
      return selectedItemsLabel
    }
  }

  const isFiltering = hasSearchBar && filter.length > 0
  const filteredOptions = (() => {
    if (!isFiltering) return options

    if (isOptionGroup) {
      return options
        .map((group) => {
          return {
            ...group,
            [optionGroupChildren]: group[optionGroupChildren].filter((el) => {
              for (const value of Object.values(el)) {
                const val = _.deburr(
                  String(value).normalize("NFD").toLowerCase()
                )
                const filterVal = _.deburr(
                  filter.normalize("NFD").toLowerCase()
                )
                if (val.includes(filterVal)) {
                  return true
                }
              }
              return false
            }),
          }
        })
        .filter((el) => {
          return el[optionGroupChildren].length > 0
        })
    } else {
      return options.filter((el) => {
        for (const value of Object.values(el)) {
          try {
            const val = _.deburr(String(value).normalize("NFD").toLowerCase())
            const filterVal = _.deburr(filter.normalize("NFD").toLowerCase())
            if (val.includes(filterVal)) {
              return true
            }
          } catch (e) {}
        }
        return false
      })
    }
  })()

  const calculateIfCheckedAllCurrentlyFiltered = () => {
    if (!isFiltering) return false

    if (isOptionGroup) {
      const allFilteredItems = filteredOptions.flatMap(
        (el) => el[optionGroupChildren]
      )
      if (allFilteredItems.length === 0) return false
      return allFilteredItems.every((el) => value.includes(el[optionValue]))
    } else {
      if (filteredOptions.length === 0) return false
      return filteredOptions.every((el) => value.includes(el[optionValue]))
    }
  }

  const calculateHasSelectedAll = () => {
    if (calculateIfCheckedAllCurrentlyFiltered()) {
      return true
    }

    if (isOptionGroup) {
      const allOptions = options
        .flatMap((el) => el[optionGroupChildren])
        .map((el) => el[optionValue])
      return allOptions.every((el) => value.includes(el))
    } else {
      return value.length === options.length
    }
  }

  const ToggleButton = () => (
    <div
      onClick={() => setIsContentVisible(!isContentVisible)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      className={clsx(
        "p-dropdown p-component p-inputwrapper",
        isFocused && "p-focus",
        isFocused && "p-inputwrapper-focus",
        disabled &&
          "p-multiselect p-component p-inputwrapper p-disabled client-project-filter-multiselect",
        value.length > 0 && "p-inputwrapper-filled"
      )}
    >
      <span className="p-dropdown-label flex align-items-center p-inputtext p-placeholder">
        {renderLabels()}
      </span>
      <div className="p-dropdown-trigger">
        <span className="p-dropdown-trigger-icon p-clickable pi pi-chevron-down"></span>
      </div>
    </div>
  )

  return (
    <div className={clsx("inline-multiselect flex flex-column", className)}>
      {!hideToggleButton && <ToggleButton />}
      {/* content */}

      <div
        className={clsx(
          "p-multiselect-panel",
          isContentVisible && "is-visible"
        )}
        style={{
          height: "100%",
          maxHeight: isContentVisible ? maxHeight : 0,
          marginTop:
            hideToggleButton ? 0
            : isContentVisible ? 8
            : 0,
        }}
      >
        <div className="p-multiselect-header">
          <InputCheckbox
            inputId="inline-multiselect-select-all"
            disabled={filteredOptions.length === 0}
            onChange={() => {
              if (isFiltering) {
                if (isOptionGroup) {
                  const filteredItems = filteredOptions
                    .flatMap((el) => el[optionGroupChildren])
                    .map((el) => el[optionValue])

                  const allFilteredSelected = filteredItems.every((el) =>
                    value.includes(el)
                  )

                  if (allFilteredSelected) {
                    onChange(
                      value.filter((v) => {
                        return !filteredItems.some((el) => el === v)
                      })
                    )
                  } else {
                    onChange([
                      ...value.filter((v) => {
                        return !filteredItems.some((el) => el === v)
                      }),
                      ...filteredItems,
                    ])
                  }
                } else {
                  const allFilteredSelected = filteredOptions.every((el) =>
                    value.includes(el[optionValue])
                  )

                  if (allFilteredSelected) {
                    onChange(
                      value.filter((v) => {
                        return !filteredOptions.some(
                          (el) => el[optionValue] === v
                        )
                      })
                    )
                  } else {
                    onChange([
                      ...value.filter((v) => {
                        return !filteredOptions.some(
                          (el) => el[optionValue] === v
                        )
                      }),
                      ...filteredOptions.map((el) => el[optionValue]),
                    ])
                  }
                }
              } else {
                if (isOptionGroup) {
                  const allOptions = options
                    .flatMap((el) => el[optionGroupChildren])
                    .map((el) => el[optionValue])

                  const allSelected = allOptions.every((el) =>
                    value.includes(el)
                  )

                  if (allSelected) {
                    onChange([])
                  } else {
                    onChange(
                      options.flatMap((el) =>
                        el[optionGroupChildren].map((el) => el[optionValue])
                      )
                    )
                  }
                } else {
                  const allSelected = value.length === options.length
                  if (allSelected) {
                    onChange([])
                  } else {
                    onChange(options.map((el) => el[optionValue]))
                  }
                }
              }
            }}
            checked={options.length > 0 ? calculateHasSelectedAll() : false}
          />
          {hasSearchBar && (
            <div className="p-multiselect-filter-container">
              <InputText
                id={"inline-multiselect-search-input"}
                autoFocus={!loading && autoFocusSearchBar}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              <span className="p-multiselect-filter-icon pi pi-search"></span>
            </div>
          )}
          {customSuffixHeaderIcon ||
            (!hideToggleButton && (
              <Button
                onClick={() => setIsContentVisible(false)}
                icon="pi pi-times"
                className="p-multiselect-close p-button-link"
                aria-label="Close"
              />
            ))}
        </div>
        {/* items list */}
        <div className="p-multiselect-items-wrapper">
          <ul
            className={clsx(
              "p-multiselect-items p-component",
              loading && "is-loading"
            )}
            style={{
              overflow: isContentVisible ? "" : "hidden",
            }}
            role="listbox"
            aria-multiselectable="true"
          >
            {loading && (
              <div className="w-full h-full flex align-items-center justify-content-center">
                <ProgressSpinner />
              </div>
            )}
            {options.length === 0 && !loading ?
              <li className="p-multiselect-empty-message">
                No hay opciones disponibles
              </li>
            : loading ?
              null
            : filteredOptions.map((el, i) => {
                if (isOptionGroup) {
                  const items = []
                  const groupChild = optionGroupTemplate(el)
                  if (groupChild) {
                    items.push(
                      <ItemGroupTemplate
                        key={el[optionValue] + i + el[optionValue]}
                      >
                        {groupChild}
                      </ItemGroupTemplate>
                    )
                  }
                  el[optionGroupChildren].forEach((item) => {
                    items.push(
                      <ItemTemplate
                        isChecked={value.some((v) => v === item[optionValue])}
                        key={item[optionValue] + item[optionValue] + i}
                        onChange={() => {
                          const index = value.findIndex(
                            (v) => v === item[optionValue]
                          )
                          const notSelected = index === -1
                          if (notSelected) {
                            onChange([...value, item[optionValue]])
                          } else {
                            onChange(
                              value.filter((v) => v !== item[optionValue])
                            )
                          }
                        }}
                      >
                        {itemTemplate(item)}
                      </ItemTemplate>
                    )
                  })
                  return items
                } else {
                  return (
                    <ItemTemplate
                      isChecked={value.some((v) => v === el[optionValue])}
                      key={el[optionValue] + i + el[optionLabel]}
                      onChange={() => {
                        const index = value.findIndex(
                          (v) => v === el[optionValue]
                        )
                        const notSelected = index === -1
                        if (notSelected) {
                          onChange([...value, el[optionValue]])
                        } else {
                          onChange(value.filter((v) => v !== el[optionValue]))
                        }
                      }}
                    >
                      {itemTemplate(el)}
                    </ItemTemplate>
                  )
                }
              })
            }
            {options.length !== 0 && filteredOptions.length === 0 && (
              <li className="p-multiselect-empty-message px-3 py-4">
                {emptyFilterMessage}
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  )
}

const ItemTemplate = ({ children, onChange, isChecked }) => {
  const checkboxId = Math.random().toString(36).substring(7)
  return (
    <li
      onClick={() => {
        onChange(!isChecked)
      }}
      className={clsx("p-multiselect-item", isChecked && "p-highlight")}
      tabIndex="0"
      role="option"
      aria-selected="false"
    >
      <InputCheckbox inputId={checkboxId} checked={isChecked} />
      <span
        title={typeof children === "string" ? children : null}
        className="inline-multiselect-item-label"
      >
        {children}
      </span>
      <span role="presentation" className="p-ink"></span>
    </li>
  )
}

const ItemGroupTemplate = ({ children }) => {
  return (
    <li
      className="p-multiselect-item-group"
      tabIndex="0"
      role="option"
      aria-selected="false"
    >
      <span
        title={typeof children === "string" ? children : null}
        className="inline-multiselect-item-group-label"
      >
        {children}
      </span>
      <span role="presentation" className="p-ink"></span>
    </li>
  )
}

const propTypes = {
  value: PropTypes.array.isRequired,
  options: PropTypes.array.isRequired,
  optionLabel: PropTypes.string.isRequired,
  optionValue: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  selectedItemsLabel: PropTypes.node,
  maxSelectedLabels: PropTypes.number,
  maxHeight: PropTypes.number,
  loading: PropTypes.bool,
  itemTemplate: PropTypes.func,
  optionGroupLabel: PropTypes.string,
  optionGroupChildren: PropTypes.string,
  optionGroupTemplate: PropTypes.func,
  selectedItemTemplate: PropTypes.func,
  className: PropTypes.string,
  customSuffixHeaderIcon: PropTypes.node,
  hideToggleButton: PropTypes.bool,
  initiallyExpanded: PropTypes.bool,
  hasSearchBar: PropTypes.bool,
  emptyFilterMessage: PropTypes.string,
  autoFocusSearchBar: PropTypes.bool,
}

InlineMultiSelectDropdown.propTypes = propTypes

export default InlineMultiSelectDropdown
